import React from 'react';
import { AlertTriangle, XCircle, AlertCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface OverlapFlag {
  tortCode: string;
  matchType: string;
  confidenceScore: string;
  ruleApplied: string;
  evidenceOnFile: string[];
  evidenceGaps: string[];
}

interface Deficiency {
  deficiencyCode: string;
  message: string;
  severity: string;
  raisedAt: string;
  clearedAt?: string;
}

interface DeficiencyPanelProps {
  claimsiqId: string;
  deficiencies: Deficiency[];
  overlapFlags?: OverlapFlag[];
  onClose: () => void;
}

const SEVERITY_STYLES: Record<string, { bg: string; border: string; iconColor: string }> = {
  CRITICAL: { bg: 'bg-red-50', border: 'border-red-200', iconColor: 'text-red-500' },
  WARNING: { bg: 'bg-amber-50', border: 'border-amber-200', iconColor: 'text-amber-500' },
  INFORMATIONAL: { bg: 'bg-blue-50', border: 'border-blue-200', iconColor: 'text-blue-500' },
};

const TORT_COLORS: Record<string, string> = {
  RLP: 'bg-green-100 text-green-700',
  PFAS: 'bg-amber-100 text-amber-700',
  VGA: 'bg-blue-100 text-blue-700',
  SMA: 'bg-purple-100 text-purple-700',
};

function getSeverityIcon(severity: string) {
  switch (severity) {
    case 'CRITICAL': return AlertTriangle;
    case 'WARNING': return AlertCircle;
    case 'INFORMATIONAL': return Info;
    default: return AlertTriangle;
  }
}

export const DeficiencyPanel: React.FC<DeficiencyPanelProps> = ({
  claimsiqId,
  deficiencies,
  overlapFlags,
  onClose,
}) => {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Deficiency Notices</h2>
            <p className="text-sm text-gray-500">{claimsiqId}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <XCircle className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <div className="space-y-3">
          {deficiencies.map((def, idx) => {
            const style = SEVERITY_STYLES[def.severity] || SEVERITY_STYLES.WARNING;
            const Icon = getSeverityIcon(def.severity);
            return (
              <div key={idx} className={`border ${style.border} ${style.bg} rounded-lg p-4`}>
                <div className="flex items-start gap-3">
                  <Icon className={`h-5 w-5 ${style.iconColor} mt-0.5 flex-shrink-0`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-mono bg-white/60 border border-gray-300 text-gray-700 px-2 py-0.5 rounded">{def.deficiencyCode}</span>
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                        def.severity === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                        def.severity === 'WARNING' ? 'bg-amber-100 text-amber-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>{def.severity}</span>
                      <span className="text-xs text-gray-500">Raised: {def.raisedAt}</span>
                      {def.clearedAt && <span className="text-xs text-green-600 font-medium">Cleared: {def.clearedAt}</span>}
                    </div>
                    <p className="text-sm text-gray-800 mt-1">{def.message}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {overlapFlags && overlapFlags.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Related Overlap Flags</h3>
            <div className="space-y-2">
              {overlapFlags.map((flag) => (
                <div key={flag.tortCode} className={`flex items-center justify-between p-2 rounded ${TORT_COLORS[flag.tortCode] || 'bg-gray-100'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold">{flag.tortCode}</span>
                    <span className="text-xs">{flag.matchType} match</span>
                    <span className="text-xs opacity-75">({flag.confidenceScore})</span>
                  </div>
                  {flag.evidenceGaps.length > 0 && (
                    <span className="text-xs text-red-600 font-medium">
                      {flag.evidenceGaps.length} evidence gap{flag.evidenceGaps.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6">
          <Button onClick={onClose} variant="outline" className="w-full">Close</Button>
        </div>
      </div>
    </div>
  );
};

export default DeficiencyPanel;